import React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Github } from 'lucide-react'
import { GradientContainer } from '../components'
import useLenisScroll from '../components/lenisScroller'

// Simple markdown renderer component
const MarkdownRenderer: React.FC<{ content: string }> = ({ content }) => {
  const renderMarkdown = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactElement[] = [];
    let currentParagraph: string[] = [];
    let listItems: string[] = [];
    let inList = false;

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const paragraphText = currentParagraph.join(' ').trim();
        if (paragraphText) {
          elements.push(
            <p key={elements.length} className="mb-4 leading-relaxed">
              {parseInlineMarkdown(paragraphText)}
            </p>
          );
        }
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={elements.length} className="mb-4 space-y-2">
            {listItems.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                {parseInlineMarkdown(item)}
              </li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Handle headings
      if (trimmedLine.startsWith('### ')) {
        flushParagraph();
        flushList();
        const headingText = trimmedLine.slice(4);
        elements.push(
          <h3 key={elements.length} className="text-2xl mb-4 mt-6 text-white">
            {parseInlineMarkdown(headingText)}
          </h3>
        );
      } else if (trimmedLine.startsWith('## ')) {
        flushParagraph();
        flushList();
        const headingText = trimmedLine.slice(3);
        elements.push(
          <h2 key={elements.length} className="text-3xl mb-4 mt-8 text-white">
            {parseInlineMarkdown(headingText)}
          </h2>
        );
      } else if (trimmedLine.startsWith('# ')) {
        flushParagraph();
        flushList();
        const headingText = trimmedLine.slice(2);
        elements.push(
          <h1 key={elements.length} className="text-4xl mb-6 mt-8 text-white">
            {parseInlineMarkdown(headingText)}
          </h1>
        );
      }
      // Handle list items
      else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        flushParagraph();
        if (!inList) {
          inList = true;
        }
        const itemText = trimmedLine.slice(2);
        listItems.push(itemText);
      }
      // Handle numbered lists
      else if (/^\d+\.\s/.test(trimmedLine)) {
        flushParagraph();
        if (!inList) {
          flushList(); // Flush any existing unordered list
          inList = true;
        }
        const itemText = trimmedLine.replace(/^\d+\.\s/, '');
        listItems.push(itemText);
      }
      // Handle empty lines
      else if (trimmedLine === '') {
        if (inList) {
          flushList();
        } else {
          flushParagraph();
        }
      }
      // Handle regular text
      else {
        if (inList) {
          flushList();
        }
        currentParagraph.push(line);
      }
    });

    // Flush remaining content
    flushParagraph();
    flushList();

    return elements;
  };

  const parseInlineMarkdown = (text: string): (string | React.ReactElement)[] => {
    const parts: (string | React.ReactElement)[] = [];
    let currentIndex = 0;
    
    // Handle bold text (**text** or __text__)
    text.replace(/(\*\*|__)(.*?)\1/g, (match, delimiter, content, offset) => {
      if (offset > currentIndex) {
        parts.push(text.slice(currentIndex, offset));
      }
      parts.push(<strong key={parts.length} className="font-semibold">{content}</strong>);
      currentIndex = offset + match.length;
      return match;
    });

    // Handle italic text (*text* or _text_)
    const remainingText = text.slice(currentIndex);
    let italicIndex = 0;
    remainingText.replace(/(\*|_)(.*?)\1/g, (match, delimiter, content, offset) => {
      const absoluteOffset = currentIndex + offset;
      if (absoluteOffset > currentIndex + italicIndex) {
        parts.push(text.slice(currentIndex + italicIndex, absoluteOffset));
      }
      parts.push(<em key={parts.length} className="italic">{content}</em>);
      italicIndex = offset + match.length;
      return match;
    });

    // Add any remaining text
    if (currentIndex + italicIndex < text.length) {
      parts.push(text.slice(currentIndex + italicIndex));
    }

    // If no markdown was found, return the original text
    if (parts.length === 0) {
      return [text];
    }

    return parts;
  };

  return (
    <div className="text-white leading-relaxed">
      {renderMarkdown(content)}
    </div>
  );
};

const ProjectDetails: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { projectName } = useParams()
  
  // Get project data from navigation state
  const project = location.state?.project

  // If no project data, redirect back to portfolio
  if (!project) {
    return (
      <div className='bg-black p-8'
           style={{
             height: '100dvh',
             minHeight: '100svh'
           }}>
        <GradientContainer 
          components={[
            <div className='text-white p-8 h-full flex flex-col items-center justify-center'>
              <h1 className="text-4xl font-stretchRegular mb-4">Project Not Found</h1>
              <p className="text-gray-400 mb-8">The requested project could not be found.</p>
              <button
                onClick={() => navigate('/portfolio')}
                className="group flex items-center gap-2 border-2 border-white/30 rounded-3xl px-6 py-3 transition-all duration-300 hover:border-white/60 hover:bg-white/10"
              >
                <ArrowLeft size={20} />
                Back to Portfolio
              </button>
            </div>
          ]}
        />
      </div>
    )
  }

  const ProjectContent: React.FC = () => {
    const scrollRef = useLenisScroll()
    return (
      <div ref={scrollRef} className='text-white p-8 h-full overflow-y-auto hide-scrollbar'>
        {/* Back button */}
        <button
          onClick={() => navigate('/portfolio')}
          className="group flex items-center gap-2 mb-8 text-white hover:text-blue-400 transition-colors duration-300 ml-auto md:ml-0 pt-safe-top md:pt-0"
        >
          <ArrowLeft size={20} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Back to Portfolio
        </button>

        {/* Project header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-5xl font-stretchRegular mb-6">
            {project.title}
          </h1>
          
          {project.description && (
            <div className="max-w-4xl">
              <MarkdownRenderer content={project.description} />
            </div>
          )}
        </div>

        {/* Technologies */}
        {project.technologies && project.technologies.length > 0 && (
          <div className="mb-8">
            <h3 className="text-2xl mb-4">Technologies Used</h3>
            <div className="flex flex-wrap gap-3">
              {project.technologies.map((tech: string, index: number) => (
                <span
                  key={index}
                  className="bg-asphalt text-paper px-4 py-2 rounded-full text-sm"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Links */}
        {(project.websiteURL || project.githubURL) && (
          <div className="mb-8">
            <h3 className="text-2xl mb-4">Project Links</h3>
            <div className="flex flex-wrap gap-4">
              {project.websiteURL && (
                <a
                  href={project.websiteURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-full border border-orange-500/30 hover:bg-orange-500/30 hover:border-orange-500/50 transition-all duration-300"
                >
                  <ExternalLink size={18} />
                  Live Website
                </a>
              )}
              
              {project.githubURL && (
                <a
                  href={project.githubURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2 bg-black text-white px-6 py-3 rounded-full hover:bg-gray-500/30 hover:border-gray-500/50 transition-all duration-300"
                >
                  <Github size={18} />
                  Source Code
                </a>
              )}
            </div>
          </div>
        )}

        {/* Project details */}
        {project.features && project.features.length > 0 && (
          <div className="mb-8">
            <h3 className="text-2xl mb-4">Key Features</h3>
            <ul className="space-y-2 text-white">
              {project.features.map((feature: string, index: number) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Additional content */}
        {project.content && (
          <div className="mb-8 pb-safe-bottom">
            <h3 className="text-2xl mb-4">Details</h3>
            <div className="max-w-4xl">
              <MarkdownRenderer content={project.content} />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='bg-black p-4 md:p-8'
         style={{
           height: '100dvh',
           minHeight: '100svh'
         }}>
      <GradientContainer 
        components={[<ProjectContent key="project-content" />]}
      />
      <div className='absolute text-white opacity-30 text-xs'
           style={{
             bottom: 'max(0.5rem, env(safe-area-inset-bottom))',
             left: 'max(0.5rem, env(safe-area-inset-left))'
           }}>
        &copy; AHMED ELGHAMRINY
      </div>
    </div>
  )
}

export default ProjectDetails